import {useRef, useState} from 'react';
import type {RouterOutputs} from '../trpc';

export interface ResponseHistoryEntry {
  id: number;
  question: string;
  response: NonNullable<RouterOutputs['chat']['createChat']>;
  imageUrl?: string;
}

export default function useResponseHistory() {
  const nextId = useRef(0);
  const [history, setHistory] = useState<{entries: ResponseHistoryEntry[]; index: number}>({
    entries: [], index: -1,
  });

  const addResponse = (question: string, response: ResponseHistoryEntry['response']) => {
    const id = nextId.current++;
    setHistory(previous => ({
      entries: [...previous.entries, {id, question, response}],
      index: previous.entries.length,
    }));
    return id;
  };

  const setImage = (id: number, imageUrl: string) => {
    setHistory(previous => ({
      ...previous,
      entries: previous.entries.map(entry => entry.id === id ? {...entry, imageUrl} : entry),
    }));
  };

  const navigate = (offset: number) => {
    setHistory(previous => previous.entries.length === 0 ? previous : ({
      ...previous,
      index: Math.max(0, Math.min(previous.entries.length - 1, previous.index + offset)),
    }));
  };

  return {
    current: history.entries[history.index],
    position: history.index + 1,
    total: history.entries.length,
    addResponse,
    setImage,
    previous: () => navigate(-1),
    next: () => navigate(1),
  };
}
